import { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import Navbar from "../../components/feature/Navbar";
import Footer from "../../components/feature/Footer";
import { fetchMediaReportById, fetchPublicMediaReports } from "../../api/dripPlan";
import type { MediaReportDetail, MediaReportItem } from "../../api/dripPlan";

function formatDate(dateStr: string) {
  if (!dateStr) return "";
  const d = new Date(dateStr);
  if (isNaN(d.getTime())) return dateStr;
  return `${d.getFullYear()}年${d.getMonth() + 1}月${d.getDate()}日`;
}

export default function MediaDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [report, setReport] = useState<MediaReportDetail | null>(null);
  const [relatedList, setRelatedList] = useState<MediaReportItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  const reportId = id ? parseInt(id, 10) : 0;

  useEffect(() => {
    if (!reportId) {
      setNotFound(true);
      setLoading(false);
      return;
    }
    let cancelled = false;
    setLoading(true);
    setNotFound(false);
    Promise.all([
      fetchMediaReportById(reportId),
      fetchPublicMediaReports(),
    ])
      .then(([detail, list]) => {
        if (cancelled) return;
        if (detail) {
          setReport(detail);
          const related = (list || []).filter((r) => r.id !== reportId).slice(0, 3);
          setRelatedList(related);
        } else {
          setNotFound(true);
        }
      })
      .catch(() => {
        if (!cancelled) setNotFound(true);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [reportId]);

  if (loading) {
    return (
      <div className="min-h-screen bg-white">
        <Navbar />
        <div className="max-w-4xl mx-auto px-6 py-20 text-center text-[#6C6C6C]">
          加载中...
        </div>
        <Footer />
      </div>
    );
  }

  if (notFound || !report) {
    return (
      <div className="min-h-screen bg-white">
        <Navbar />
        <div className="max-w-4xl mx-auto px-6 py-20 text-center">
          <h2 className="text-2xl font-bold text-[#1E1E1E] mb-4">报道不存在</h2>
          <button
            onClick={() => navigate("/about")}
            className="bg-[#2A7A62] text-white px-6 py-3 rounded-lg font-bold hover:bg-[#1E5A4A] transition-colors duration-300 cursor-pointer whitespace-nowrap"
          >
            返回关于我们
          </button>
        </div>
        <Footer />
      </div>
    );
  }

  const hasContent = report.content && report.content.trim().length > 0;
  const hasArticleLink = report.articleLink && report.articleLink.trim().length > 0;

  return (
    <div className="min-h-screen bg-white">
      <Navbar />

      {/* 面包屑导航 */}
      <div className="bg-[#F9F9F9] py-4 px-6">
        <div className="max-w-4xl mx-auto">
          <div className="flex items-center gap-2 text-sm text-[#6C6C6C]">
            <Link to="/" className="hover:text-[#2A7A62] transition-colors duration-300">
              首页
            </Link>
            <i className="ri-arrow-right-s-line"></i>
            <Link to="/about" className="hover:text-[#2A7A62] transition-colors duration-300">
              关于我们
            </Link>
            <i className="ri-arrow-right-s-line"></i>
            <span className="text-[#1E1E1E]">媒体报道</span>
          </div>
        </div>
      </div>

      <article className="max-w-4xl mx-auto px-6 py-12">
        <header className="mb-8">
          {report.sourceLogo ? (
            <div className="mb-6 flex items-center justify-center">
              <img
                src={report.sourceLogo}
                alt={report.sourceName}
                className="h-16 object-contain"
              />
            </div>
          ) : null}

          <div className="flex items-center gap-3 mb-4 flex-wrap">
            <span className="inline-block bg-[#2A7A62]/10 text-[#2A7A62] px-4 py-1 rounded-full text-sm font-medium">
              {report.sourceName || "媒体报道"}
            </span>
          </div>

          <h1 className="text-4xl font-bold text-[#1E1E1E] mb-6 leading-tight">
            {report.title}
          </h1>

          <div className="flex items-center gap-6 text-[#6C6C6C] text-sm pb-6 border-b border-[#E0E0E0]">
            <div className="flex items-center gap-2">
              <i className="ri-calendar-line"></i>
              <span>{formatDate(report.publicationDate)}</span>
            </div>
            <div className="flex items-center gap-2">
              <i className="ri-building-line"></i>
              <span>来源：{report.sourceName || "—"}</span>
            </div>
          </div>

          <div className="flex items-center gap-4 mt-6">
            <span className="text-sm text-[#6C6C6C]">分享到：</span>
            <button
              type="button"
              className="w-10 h-10 rounded-full bg-[#09BB07] text-white flex items-center justify-center hover:opacity-80 transition-opacity duration-300 cursor-pointer"
            >
              <i className="ri-wechat-fill text-lg"></i>
            </button>
            <button
              type="button"
              className="w-10 h-10 rounded-full bg-[#6C6C6C] text-white flex items-center justify-center hover:opacity-80 transition-opacity duration-300 cursor-pointer"
            >
              <i className="ri-link text-lg"></i>
            </button>
          </div>
        </header>

        {hasContent ? (
          <div
            className="prose prose-lg max-w-none mb-12"
            dangerouslySetInnerHTML={{ __html: report.content }}
            style={{
              fontSize: "18px",
              lineHeight: "1.8",
              color: "#1E1E1E",
            }}
          />
        ) : (
          <div className="mb-12 text-[#6C6C6C] leading-relaxed">
            {report.summary || "暂无正文内容。"}
          </div>
        )}

        {hasArticleLink && (
          <div className="mb-8">
            <a
              href={report.articleLink!}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 bg-[#2A7A62] text-white px-6 py-3 rounded-lg font-medium hover:bg-[#1E5A4A] transition-colors duration-300"
            >
              阅读原文
              <i className="ri-external-link-line"></i>
            </a>
          </div>
        )}

        <div className="flex items-center justify-between pt-8 border-t border-[#E0E0E0]">
          <button
            type="button"
            onClick={() => navigate("/about")}
            className="flex items-center gap-2 text-[#2A7A62] font-medium hover:gap-3 transition-all duration-300 cursor-pointer"
          >
            <i className="ri-arrow-left-line"></i>
            返回媒体报道列表
          </button>
        </div>
      </article>

      {relatedList.length > 0 && (
        <section className="bg-[#F9F9F9] py-16 px-6">
          <div className="max-w-6xl mx-auto">
            <h2 className="text-3xl font-bold text-[#1E1E1E] mb-8 text-center">相关报道</h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              {relatedList.map((r) => (
                <Link
                  key={r.id}
                  to={`/media/${r.id}`}
                  className="bg-white rounded-xl shadow-lg overflow-hidden hover:shadow-xl transition-all duration-300 hover:-translate-y-1 cursor-pointer group"
                >
                  <div className="h-32 bg-[#F9F9F9] flex items-center justify-center p-6 border-b border-[#E0E0E0]">
                    {r.sourceLogo ? (
                      <img
                        src={r.sourceLogo}
                        alt={r.sourceName}
                        className="max-w-full max-h-full object-contain"
                      />
                    ) : (
                      <span className="text-[#6C6C6C] font-medium">{r.sourceName || "媒体报道"}</span>
                    )}
                  </div>
                  <div className="p-6">
                    <div className="flex items-center gap-2 mb-3">
                      <span className="inline-block bg-[#2A7A62]/10 text-[#2A7A62] px-3 py-1 rounded-full text-xs font-medium">
                        {r.sourceName}
                      </span>
                      <span className="text-xs text-[#6C6C6C]">{formatDate(r.publicationDate)}</span>
                    </div>
                    <h3 className="text-lg font-bold text-[#1E1E1E] mb-3 line-clamp-2 group-hover:text-[#2A7A62] transition-colors duration-300">
                      {r.title}
                    </h3>
                    <div className="flex items-center text-[#2A7A62] font-medium text-sm">
                      阅读全文
                      <i className="ri-arrow-right-line ml-2 group-hover:translate-x-1 transition-transform duration-300"></i>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      <Footer />

      <style>{`
        .prose h3 {
          font-size: 24px;
          font-weight: bold;
          color: #1E1E1E;
          margin-top: 32px;
          margin-bottom: 16px;
        }
        .prose p {
          margin-bottom: 20px;
          text-align: justify;
        }
        .line-clamp-2 {
          display: -webkit-box;
          -webkit-line-clamp: 2;
          -webkit-box-orient: vertical;
          overflow: hidden;
        }
      `}</style>
    </div>
  );
}
